import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ContextService } from '@bcef/ia-shared';
import { JsonConvert } from 'json2typescript';
import { map, Observable } from 'rxjs';

import { SimulationForConnectedCustomerRequest } from '@features/loan/interfaces/simulation-for-connected-customer-request.interface';
import { SimulationForNotConnectedCustomerRequest } from '@features/loan/interfaces/simulation-for-not-connected-customer-request.interface';

import {
  CatalogResponse,
  LoanCreationResponse,
  SimulationForConnectedCustomerResponse,
  SimulationForNotConnectedUserResponse,
  UserChoices,
} from '../interfaces';
import { SavedSimulationResponse } from '../interfaces/saved-simulation-confirmation.interface';
import {
  Catalog,
  ConnectedSimulation,
  LoanCharacteristics,
  NotConnectedSimulation,
  SavedSimulationConfirmation,
} from '../models';
import { PublishablesAreas } from '../models/publishables-areas-response.model';
import { PublishablesAreasResponse } from './publishables-areas-response.interface';

@Injectable({
  providedIn: 'root',
})
export class LoanService {
  private readonly http = inject(HttpClient);

  private readonly jsonConvert = inject(JsonConvert);

  private readonly contextService = inject(ContextService);

  public getPublishablesAreas(): Observable<PublishablesAreas> {
    return this.http
      .get<PublishablesAreasResponse>(
        `/content/dam/app/zones-publiables/ia-simulateur-ppi/ia-simulateur-ppi-zones-publiables-fr-${this.contextService.typology}-V1.json`,
      )
      .pipe(map((response) => this.jsonConvert.deserialize(response, PublishablesAreas)));
  }

  public getCatalog(): Observable<Catalog> {
    return this.http
      .get<CatalogResponse>('/rgg/personal-loans/v1/catalogs')
      .pipe(map((data) => this.jsonConvert.deserialize(data, Catalog)));
  }

  public postLoanCreation(
    loanTypeId: string,
    isCollective: boolean,
    mobility4YouParams?: { partnerId: string; partnerProjectId: string },
  ): Observable<LoanCharacteristics> {
    return this.http
      .post<LoanCreationResponse>(
        `/rgg/personal-loans/v3/me/projects${this.constructPostLoanCreationUrlParams(isCollective, mobility4YouParams)}`,
        {
          loanItemId: loanTypeId,
        },
      )
      .pipe(map((data) => this.jsonConvert.deserialize(data, LoanCharacteristics)));
  }

  private constructPostLoanCreationUrlParams(
    isCollective: boolean,
    mobility4YouParams?: { partnerId: string; partnerProjectId: string },
  ): string {
    let url = '';
    if (isCollective) {
      url = '?clientTypeIndicator=C';
    } else if (mobility4YouParams) {
      url = `?partnerId=${mobility4YouParams.partnerId}&partnerProjectId=${mobility4YouParams.partnerProjectId}`;
    }
    return url;
  }

  public getNotConnectedUserSimulation(
    loanTypeId: string,
    userChoices: UserChoices,
  ): Observable<NotConnectedSimulation> {
    const parameters: Partial<SimulationForNotConnectedCustomerRequest> = {
      loanItemId: loanTypeId,
      amount: userChoices.amount ?? 0,
    };
    if (userChoices.byDuration) {
      parameters.duration = userChoices.duration ?? 0;
    } else {
      parameters.monthlyPaymentNotExceeded = userChoices.monthlyPayment ?? 0;
    }
    return this.http
      .get<SimulationForNotConnectedUserResponse>(`/rgg/personal-loans/v1/simulations`, {
        params: parameters,
      })
      .pipe(map((data) => this.jsonConvert.deserialize(data, NotConnectedSimulation)));
  }

  public getConnectedCustomerSimulation(
    projectId: string,
    userChoices: UserChoices,
  ): Observable<ConnectedSimulation> {
    const parameters: Partial<SimulationForConnectedCustomerRequest> = {
      amount: userChoices.amount ?? 0,
    };
    if (userChoices.byDuration) {
      parameters.duration = userChoices.duration ?? 0;
    } else {
      parameters.monthlyPaymentNotExceeded = userChoices.monthlyPayment ?? 0;
    }
    const queryParams = new HttpParams({ fromObject: parameters });
    return this.http
      .get<SimulationForConnectedCustomerResponse>(
        `/rgg/personal-loans/v2/projects/${projectId}/simulations`,
        {
          params: queryParams,
        },
      )
      .pipe(map((data) => this.jsonConvert.deserialize(data, ConnectedSimulation)));
  }

  public saveConnectedCustomerSimulation(
    projectId: string,
    simulationReference: string,
  ): Observable<SavedSimulationConfirmation> {
    return this.http
      .put<SavedSimulationResponse>(`/rgg/personal-loans/v1/projects/${projectId}/simulation`, {
        referenceSimulation: simulationReference,
      })
      .pipe(map((data) => this.jsonConvert.deserialize(data, SavedSimulationConfirmation)));
  }
}
